import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";

// Mirrors the Google Form options exactly
const INTERESTS = [
  "Mutual Funds and SIPs",
  "Life Insurance",
  "Health Insurance",
  "Motor Insurance",
  "Retirement Planning",
  "Tax Saving Investments",
  "General Financial Consultation",
  "Business Insurance",
  "Marine Insurance",
  "Other Insurances",
];

const HEARD_FROM = [
  "Website",
  "Google Search",
  "Social Media",
  "Referral from Friend/Family",
  "Advertisement",
  "Existing Client",
  "Local Office Visit",
  "Other",
];

export default function LeadForm({
  title = "Get a Free Consultation",
  subtitle = "Share your details. Vinay or a senior advisor will call you back within 2 business hours.",
  source = "lead_form",
  defaultInterest,
  variant = "card",
  testIdPrefix = "lead",
}) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    interest: defaultInterest || "",
    how_heard: "Website",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and contact number.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({ ...form, source });
      setDone(true);
      toast.success("Thanks! We'll reach out shortly.");
    } catch (err) {
      toast.error("Could not submit. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  const wrapClass =
    variant === "card"
      ? "rounded-2xl bg-white p-6 sm:p-8 soft-shadow border border-slate-100"
      : "";

  if (done) {
    return (
      <div className={wrapClass} data-testid={`${testIdPrefix}-form-success`}>
        <div className="flex flex-col items-center text-center py-6">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <CheckCircle2 className="h-6 w-6" />
          </span>
          <h3 className="font-display text-xl font-semibold mt-4 text-slate-900">
            Request received
          </h3>
          <p className="mt-2 text-sm text-slate-600 max-w-sm">
            A senior advisor from Samota Insurance will reach out within 2 business hours.
            For urgent queries, please call <a className="text-blue-900 font-medium" href="tel:+919821237924">+91 98212 37924</a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className={wrapClass} data-testid={`${testIdPrefix}-form`}>
      <div className="mb-5">
        <h3 className="font-display text-xl sm:text-2xl font-semibold text-slate-900">{title}</h3>
        <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{subtitle}</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label htmlFor={`${testIdPrefix}-name`}>Full name<span className="text-rose-500">*</span></Label>
          <Input
            id={`${testIdPrefix}-name`}
            data-testid={`${testIdPrefix}-input-name`}
            value={form.name}
            onChange={set("name")}
            placeholder="e.g. Priya Sharma"
            required
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor={`${testIdPrefix}-phone`}>Contact number<span className="text-rose-500">*</span></Label>
          <Input
            id={`${testIdPrefix}-phone`}
            data-testid={`${testIdPrefix}-input-phone`}
            value={form.phone}
            onChange={set("phone")}
            placeholder="+91 98XXXXXXXX"
            required
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor={`${testIdPrefix}-email`}>Email address</Label>
          <Input
            id={`${testIdPrefix}-email`}
            data-testid={`${testIdPrefix}-input-email`}
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="you@example.com"
            className="mt-1.5"
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor={`${testIdPrefix}-city`}>City of residence</Label>
          <Input
            id={`${testIdPrefix}-city`}
            data-testid={`${testIdPrefix}-input-city`}
            value={form.city}
            onChange={set("city")}
            placeholder="Thane, Mumbai…"
            className="mt-1.5"
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor={`${testIdPrefix}-interest`}>Which services are you interested in?</Label>
          <Select value={form.interest} onValueChange={set("interest")}>
            <SelectTrigger className="mt-1.5" data-testid={`${testIdPrefix}-select-interest`}>
              <SelectValue placeholder="Choose a product or service" />
            </SelectTrigger>
            <SelectContent>
              {INTERESTS.map((i) => (
                <SelectItem key={i} value={i} data-testid={`${testIdPrefix}-option-${i.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {i}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor={`${testIdPrefix}-heard`}>How did you hear about us?</Label>
          <Select value={form.how_heard} onValueChange={set("how_heard")}>
            <SelectTrigger className="mt-1.5" data-testid={`${testIdPrefix}-select-heard`}>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {HEARD_FROM.map((h) => (
                <SelectItem key={h} value={h} data-testid={`${testIdPrefix}-heard-${h.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
                  {h}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor={`${testIdPrefix}-message`}>Anything specific you'd like us to address?</Label>
          <Textarea
            id={`${testIdPrefix}-message`}
            data-testid={`${testIdPrefix}-input-message`}
            value={form.message}
            onChange={set("message")}
            placeholder="Existing policies, age of family members, goals…"
            rows={3}
            className="mt-1.5"
          />
        </div>
      </div>

      <Button
        type="submit"
        disabled={loading}
        data-testid={`${testIdPrefix}-submit`}
        className="mt-6 w-full bg-blue-900 hover:bg-blue-950 text-white h-11"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request callback"}
      </Button>
      <p className="text-[11px] text-slate-400 mt-3 text-center">
        By submitting, you agree to be contacted about your enquiry. No spam, ever.
      </p>
    </form>
  );
}
